import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {Asset} from '../../../models/asset.class';
import {RouteAssignmentManagerService} from '../../../../../data/routes/route-assignment-manager.service';
import {RouteAssignment} from '../../../../../shared/models/route-assignment';
import {ActionMenuItem} from '../../../../../shared/models/action-menu-item.class';
import {ShortDateOrTimePipe} from '../../../../../shared/formatting/short-date-or-time.pipe';
import {VehiclesManagerService} from '../../../../../data/vehicles/vehicles-manager.service';
import moment from 'moment/moment';
import { Router } from '@angular/router';
import { MainRoute, RootRoute } from '../../../../../shared/models/angular-routing';
import { InsightsRoute, ShiftDetailRoute } from '../../../../insights/insights.routes';
import { RouteAssignmentService } from '../../../../../data/routes/route-assignment.service';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { ToastService } from '../../../../../shared/services/toast.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { LocationApiService } from '../../../../../data/location-api/location-api.service';
import { QueueMenuItem, QueueMenuItemComponent } from '../../../../../shared/components/queue-menu-item/queue-menu-item.component';

export enum QueueType {
  VEHICLES,
  ROUTES,
}

@Component({
  selector: 'app-route-assignment-queue',
  templateUrl: './route-assignment-queue.component.html',
  styleUrl: './route-assignment-queue.component.scss',
  imports: [MatProgressSpinner, CdkDropList, CdkDrag, QueueMenuItemComponent],
  providers: [ShortDateOrTimePipe],
})
export class RouteAssignmentQueueComponent implements OnInit, OnChanges, OnDestroy {
  @Input() vehicle: Asset;
  @Input() routeConfigId: number;
  @Input() routeId: number;
  @Input() timeFromHoursFilter: number;

  allRecentRouteAssignments: RouteAssignment[];
  filteredAssignments: RouteAssignment[];
  items: QueueMenuItem[] = [];

  percentCompleteMap = new Map<number, number>();
  percentCompleteIntervalId: NodeJS.Timeout;

  private readonly openSubscriptions = Array<Subscription>();

  constructor(
    private router: Router,
    private routeAssignmentManager: RouteAssignmentManagerService,
    private routeAssignmentService: RouteAssignmentService,
    private locationApi: LocationApiService,
    private vehicleManager: VehiclesManagerService,
    private shortDateOrTimePipe: ShortDateOrTimePipe,
    private toast: ToastService
  ) {}

  ngOnInit() {
    const assignmentsSubscription = this.routeAssignmentManager.recentRouteAssignments$.subscribe(assignmentsUpdate => {
      // console.log(assignmentsUpdate);
      this.allRecentRouteAssignments = assignmentsUpdate.state;
      this.reloadAssignments();
    });
    this.openSubscriptions.push(assignmentsSubscription);

    // update percent complete every 30s
    this.percentCompleteIntervalId = setInterval(() => {
      this.updatePercentComplete();
    }, 30 * 1000);
  }

  ngOnChanges(changes: SimpleChanges) {
    this.reloadAssignments();
  }

  ngOnDestroy() {
    this.openSubscriptions.forEach(subscription => subscription.unsubscribe());

    if (!!this.percentCompleteIntervalId) {
      clearInterval(this.percentCompleteIntervalId);
    }
    this.percentCompleteMap.clear();
  }

  private updatePercentComplete() {
    const queueType = !!this.vehicle ? QueueType.ROUTES : QueueType.VEHICLES;
    const inProgressAssignments = this.filteredAssignments?.filter(a => a.onAssignmentFrom && !a.completed && !a.declined);
    inProgressAssignments?.forEach(inProgressAssignment => {
      if (inProgressAssignment) {
        this.locationApi.getRouteAssignmentTracking(inProgressAssignment.configId, inProgressAssignment.routeId).then(response => {
          const status = response.find(status => status.sessionkey === inProgressAssignment.shiftId);
          this.percentCompleteMap.set(inProgressAssignment.id, status?.percent_complete ?? 0);
          this.items = this.filteredAssignments.map(assignment => {
            return this.assignmentToItem(assignment, queueType);
          });
        }).catch(error => {
          console.warn(`${error} Route Configuration ID=${inProgressAssignment.configId}`);
          this.percentCompleteMap.set(inProgressAssignment.id, -1);
          this.items = this.filteredAssignments.map(assignment => {
            return this.assignmentToItem(assignment, queueType);
          });
        });
      }
    });
  }

  private reloadAssignments() {
    if (!!this.allRecentRouteAssignments) {
      const queueType = !!this.vehicle ? QueueType.ROUTES : QueueType.VEHICLES;
      this.filteredAssignments = RouteAssignment.sortByStatus(this.filterBySubject(this.allRecentRouteAssignments, queueType));
      this.updatePercentComplete();
      this.items = this.filteredAssignments.map(assignment => {
        return this.assignmentToItem(assignment, queueType);
      });
    }
  }

  private filterBySubject(assignments: RouteAssignment[], queueType: QueueType) {
    if (queueType === QueueType.ROUTES) {
      // assignments for the shift
      return assignments.filter(assignment => {
        return this.vehicle.shiftId ? assignment.shiftId === this.vehicle.shiftId : assignment.vehicleId === this.vehicle.id;
      });
    } else {
      return assignments.filter(assignment => {
        // assignments in last 24 hours for the route
        return (
          assignment.routeId === this.routeId &&
          assignment.configId === this.routeConfigId &&
          moment(assignment.created).isAfter(moment().subtract(24, 'hour'))
        );
      });
    }
  }

  private assignmentToItem(assignment: RouteAssignment, type: QueueType): QueueMenuItem {
    return new QueueMenuItem(
      assignment.id,
      type === QueueType.VEHICLES ? 'directions_car' : 'route',
      type === QueueType.VEHICLES ? this.getVehicleName(assignment.vehicleId) : assignment.routeName,
      this.getStatus(assignment),
      null,
      null,
      () => (!!assignment.onAssignmentFrom && !!assignment.completed) || !!assignment.declined,
      () => !!assignment.onAssignmentFrom && !assignment.completed,
      () => !assignment.onAssignmentFrom && !assignment.declined,
      () =>
        !!assignment.onAssignmentFrom &&
        this.router.navigate(
          [
            `/${RootRoute.MAIN}`,
            MainRoute.INSIGHTS,
            InsightsRoute.SHIFT_DETAIL,
            assignment.shiftId,
            ShiftDetailRoute.ASSIGNMENT,
            assignment.id,
          ],
          {}
        ),
      () => this.deleteRouteAssignment(assignment),
    );
  }

  deleteRouteAssignment(routeAssignment: RouteAssignment) {
    this.routeAssignmentService
      .deleteRouteAssignment(routeAssignment)
      .then(() => {
        this.toast.short(`Route '${routeAssignment.routeName}' unassigned.`);
      })
      .catch(error => {
        this.toast.longer(`Route '${routeAssignment.routeName}' could not be unassigned. Error: ${error}`);
      });
  }

  // enable only for vehicle queue
  drop(event: CdkDragDrop<QueueMenuItem[]>) {
    const completedLength = this.filteredAssignments.filter(assignment => assignment.completed).length;
    const allLength = this.filteredAssignments.length;
    if (completedLength === allLength) {
      // doing nothing, no items to re-order
      return;
    }
    const indexOfFirstIncomplete = completedLength;
    let indexOfFirstOrderable = completedLength;
    while (indexOfFirstOrderable < allLength - 1 && this.filteredAssignments[indexOfFirstOrderable].onAssignmentFrom) {
      indexOfFirstOrderable++;
    }
    if (event.previousIndex < indexOfFirstOrderable || event.currentIndex < indexOfFirstOrderable) {
      // doing nothing, cannot touch completed assignments
      this.toast.short('Cannot re-order already completed or in-progress assignments.');
      return;
    }
    const routeAssignmentIds = this.items.map(item => +item.id);
    moveItemInArray(routeAssignmentIds, event.previousIndex, event.currentIndex);
    const incompleteAssignmentIds = routeAssignmentIds.slice(indexOfFirstIncomplete);
    this.routeAssignmentService.reOrderRouteAssignments(this.vehicle.id, this.vehicle.shiftId, incompleteAssignmentIds).catch(error => {
      console.error(error);
      this.toast.long(error);
    });
  }

  private getVehicleName(vehicleId: number): string {
    return this.vehicleManager.getVehicle(vehicleId)?.label;
  }

  private getStatus(assignment: RouteAssignment): string {
    if (!!assignment.completed && !assignment.cancelled) {
      return 'Completed ' + this.shortDateOrTimePipe.transform(new Date(assignment.completed));
    } else if (!!assignment.completed) {
      return 'Cancelled ' + this.shortDateOrTimePipe.transform(new Date(assignment.completed));
    } else if (assignment.declined) {
      return 'Declined ' + this.shortDateOrTimePipe.transform(new Date(assignment.declined));
    } else if (!!assignment.onAssignmentFrom) {
      const percentComplete = this.percentCompleteMap.get(assignment.id) ?? 0;
      const pctCompleteStr = percentComplete === -1 ? 'N/A' : (percentComplete * 100).toFixed(1);
      return `In Progress ${pctCompleteStr}%`;
    } else {
      return 'Assigned';
    }
  }
}
